import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useCart } from '../contexts/CartContext';
import { api } from '../utils/api';

const STORAGE_PENDING_URLS = 'checkoutPendingUrls';
const STORAGE_CURRENT_INDEX = 'checkoutCurrentIndex';

export default function CartPanel({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();
  const { items, updateQuantity, removeItem, saveCartForCheckout, restoreFromPendingCheckout } = useCart();
  const [itemDetails, setItemDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Fetch item details for all cart items
  useEffect(() => {
    if (!isOpen || items.length === 0) {
      setItemDetails({});
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const detailsPromises = items.map(async (cartItem) => {
          try {
            const res = await api(`/api/items/${cartItem.id}`, {});
            if (!res.ok) {
              // User-friendly error messages
              if (res.status === 401) {
                return { id: cartItem.id, data: null, error: 'Please sign in to view item details' };
              } else if (res.status === 404) {
                return { id: cartItem.id, data: null, error: 'Item no longer available' };
              } else {
                return { id: cartItem.id, data: null, error: 'Unable to load item details' };
              }
            }
            const data = await res.json();
            return { id: cartItem.id, data };
          } catch (err) {
            // Don't log expected errors (401s are handled above)
            if (err.message && !err.message.includes('401')) {
              console.error(`Error loading item ${cartItem.id}:`, err);
            }
            return { id: cartItem.id, data: null, error: 'Unable to load item details' };
          }
        });

        const details = await Promise.all(detailsPromises);
        if (!cancelled) {
          const detailsMap = {};
          details.forEach(({ id, data, error }) => {
            detailsMap[id] = { ...data, error };
          });
          setItemDetails(detailsMap);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, items]);

  // Restore cart from pending checkout when user signs in (e.g. after modal sign-in)
  useEffect(() => {
    if (isSignedIn) {
      restoreFromPendingCheckout();
    }
  }, [isSignedIn, restoreFromPendingCheckout]);

  // Calculate subtotal
  const calculateSubtotal = () => {
    return items.reduce((total, cartItem) => {
      const details = itemDetails[cartItem.id];
      if (details && details.price && !details.error) {
        return total + details.price * cartItem.quantity;
      }
      return total;
    }, 0);
  };

  const subtotal = calculateSubtotal();

  // Handle quantity change
  const handleQuantityChange = (id, delta) => {
    const cartItem = items.find((item) => item.id === id);
    if (cartItem) {
      const newQuantity = cartItem.quantity + delta;
      if (newQuantity <= 0) {
        removeItem(id);
      } else {
        updateQuantity(id, newQuantity);
      }
    }
  };

  // Check if cart has any out-of-stock or insufficient stock items
  const hasStockIssues = () => {
    return items.some(cartItem => {
      const details = itemDetails[cartItem.id];
      if (!details || details.error) return false;
      const stockCount = details.stockCount ?? 0;
      return stockCount <= 0 || cartItem.quantity > stockCount;
    });
  };

  // Handle proceed to checkout: require sign-in, then call create-checkout and redirect to Stripe
  const handleCheckout = async () => {
    if (items.length === 0) return;

    // Require sign-in before checkout; save cart so it's restored after they sign in
    if (!isSignedIn) {
      saveCartForCheckout(items);
      onClose?.();
      const returnUrl = window.location.href;
      navigate(`/sign-in?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }
    
    // Validate stock before proceeding
    if (hasStockIssues()) {
      setError('Some items in your cart are out of stock or have insufficient quantity. Please update your cart.');
      return;
    }
    
    setCheckoutLoading(true);
    setError(null);
    try {
      const cartItems = items.map(({ id, quantity }) => ({ itemId: id, quantity }));
      const origin = window.location.origin || '';
      const successUrl = origin ? `${origin.replace(/\/$/, '')}/checkout/success` : undefined;
      const cancelUrl = origin ? `${origin.replace(/\/$/, '')}/checkout/cancel` : undefined;
      const res = await api('/api/payments/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartItems, successUrl, cancelUrl }),
        getToken,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Log full error for debugging
        console.error('Checkout error:', { status: res.status, data });
        // Handle stock validation errors from backend
        if (data.details && Array.isArray(data.details)) {
          setError(`Stock issues: ${data.details.join('; ')}`);
        } else {
          const errorMsg = data.error || data.stripeError || data.message || `Checkout failed (${res.status})`;
          setError(errorMsg);
        }
        return;
      }
      const urls = data.checkoutSessionUrls;
      if (!urls || urls.length === 0) {
        setError('No checkout session returned.');
        return;
      }
      sessionStorage.setItem(STORAGE_PENDING_URLS, JSON.stringify(urls));
      sessionStorage.setItem(STORAGE_CURRENT_INDEX, '0');
      window.location.href = urls[0];
    } catch (err) {
      setError(err.message || 'Checkout failed.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[45] transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold">Shopping Cart</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close cart"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Cart items list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && items.length > 0 && (
            <div className="flex items-center justify-center py-8">
              <p className="text-slate-500 dark:text-slate-400">Loading items...</p>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 mb-4">
              {error}
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-slate-300 dark:text-slate-600 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p className="text-lg text-slate-500 dark:text-slate-400">
                Your cart is empty
              </p>
            </div>
          )}

          {!loading &&
            items.length > 0 &&
            items.map((cartItem) => {
              const details = itemDetails[cartItem.id];
              const imageUrl = details?.imageUrls?.[0];
              const title = details?.title || 'Loading...';
              const price = details?.price || 0;
              const stockCount = details?.stockCount ?? 0;
              const hasError = details?.error;
              const isOutOfStock = stockCount <= 0;
              const insufficientStock = stockCount > 0 && cartItem.quantity > stockCount;

              return (
                <div
                  key={cartItem.id}
                  className="flex gap-4 p-4 border-b border-slate-200 dark:border-slate-700 last:border-b-0"
                >
                  {/* Item image */}
                  <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={title}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Item details */}
                  <div className="flex-1 flex flex-col gap-2 min-w-0">
                    <h3 className="font-medium text-sm line-clamp-2">{title}</h3>
                    {hasError ? (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {details.error || 'Error loading item'}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                          ${price.toFixed(2)}
                        </p>
                        {isOutOfStock && (
                          <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                            ⚠️ Out of stock - remove from cart
                          </p>
                        )}
                        {insufficientStock && (
                          <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                            ⚠️ Only {stockCount} available (you have {cartItem.quantity})
                          </p>
                        )}
                      </>
                    )}

                    {/* Quantity controls */}
                    <div className="flex items-center gap-2 mt-auto">
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(cartItem.id, -1)}
                        className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-300 dark:active:bg-slate-600 flex items-center justify-center transition-colors touch-manipulation"
                        aria-label="Decrease quantity"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M20 12H4"
                          />
                        </svg>
                      </button>
                      <span className="w-12 text-center font-medium">
                        {cartItem.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(cartItem.id, 1)}
                        className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-300 dark:active:bg-slate-600 flex items-center justify-center transition-colors touch-manipulation"
                        aria-label="Increase quantity"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Footer with subtotal and checkout button */}
        {items.length > 0 && (
          <div className="border-t border-slate-200 dark:border-slate-700 p-4 pb-24 space-y-4 bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between text-lg font-semibold">
              <span>Subtotal:</span>
              <span className="text-indigo-600 dark:text-indigo-400">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkoutLoading || hasStockIssues()}
              className="w-full py-4 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors touch-manipulation"
            >
              {checkoutLoading ? 'Redirecting to checkout…' : hasStockIssues() ? 'Fix stock issues to checkout' : 'Proceed to Checkout'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
